import React from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart as ReAreaChart, Area,
  BarChart as ReBarChart, Bar, RadialBarChart, RadialBar, LabelList,
  Rectangle
} from 'recharts';

export const CustomLineChart = ({ data, color = "#3B82F6", height = 200 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <LineChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
      <XAxis 
        dataKey="date" 
        axisLine={false} 
        tickLine={false} 
        tick={{ fontSize: 10, fill: '#9CA3AF' }}
        interval={Math.floor(data.length / 3)}
      />
      <YAxis hide />
      <Tooltip 
        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
      />
      <Line 
        type="monotone" 
        dataKey="value" 
        stroke={color} 
        strokeWidth={2} 
        dot={false}
      />
    </LineChart>
  </ResponsiveContainer>
);

export const SteppedPointChart = ({ data, color = "#3B82F6", height = 200, variant = 'default' }) => {
  const blueBase = '#3B82F6';
  const indigoMid = '#818CF8';
  const aquaTop = '#2DD4BF';
  const amberTop = '#FBBF24';

  let lineColors = [blueBase, indigoMid, color];
  let dotColor = color;
  let textColor = blueBase; 
  let midColor = indigoMid;

  if (variant === 'hybrid-aqua') {
    lineColors = [blueBase, indigoMid, aquaTop];
    dotColor = aquaTop;
    textColor = blueBase; 
    midColor = indigoMid;
  } else if (variant === 'hybrid-amber') {
    lineColors = [blueBase, indigoMid, amberTop];
    dotColor = amberTop;
    textColor = blueBase; 
    midColor = indigoMid;
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data.slice(-7)} margin={{ top: 50, right: 30, left: 30, bottom: 20 }}>
        <defs>
          <linearGradient id={`steppedGradient-${variant}-${color}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={lineColors[0]} stopOpacity={0.8}/>
            <stop offset="50%" stopColor={lineColors[1]} stopOpacity={1}/>
            <stop offset="100%" stopColor={lineColors[2]} stopOpacity={0.9}/>
          </linearGradient>
          {/* Glass fill for capsules/dots - Lowered opacity for better glass morphism */}
          <linearGradient id={`capsuleGlass-${variant}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={dotColor} stopOpacity={0.5}/>
            <stop offset="100%" stopColor={dotColor} stopOpacity={0.1}/>
          </linearGradient>
          {/* Enhanced Whitish Shade Element (High-fidelity highlight) */}
          <linearGradient id="glassShade" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
            <stop offset="60%" stopColor="#FFFFFF" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
          <filter id="innerGlow">
            <feFlood floodColor="white" floodOpacity="0.5" result="offsetColor" />
            <feComposite in="offsetColor" in2="SourceGraphic" operator="in" result="highlight" />
          </filter>
        </defs>
        <CartesianGrid vertical={true} horizontal={false} strokeDasharray="3 3" stroke="#F3F4F6" />
        <XAxis 
          dataKey="date" 
          axisLine={false} 
          tickLine={false} 
          tick={{ fontSize: 10, fill: '#9CA3AF', fontWeight: '600' }}
          dy={15}
          tickFormatter={(val) => val.split(' ')[1].replace(',', '')} 
        />
        <YAxis hide />
        <Tooltip cursor={{ stroke: '#E5E7EB', strokeWidth: 1 }} content={() => null} />
        <Line 
          type="monotone" 
          dataKey="value" 
          stroke={`url(#steppedGradient-${variant}-${color})`} 
          strokeWidth={4} 
          dot={({ cx, cy }) => (
            <g>
              {/* Glassy Point */}
              <circle cx={cx} cy={cy} r={8} fill={`url(#capsuleGlass-${variant})`} stroke={dotColor} strokeWidth={1} />
              <circle cx={cx} cy={cy} r={5} fill="url(#glassShade)" opacity={0.8} />
            </g>
          )}
          activeDot={{ r: 8, strokeWidth: 0 }}
        >
          <LabelList 
            dataKey="value" 
            position="top" 
            content={({ x, y, value }) => (
              <g>
                {/* Frosted Capsule Base */}
                <rect 
                  x={x - 24} 
                  y={y - 38} 
                  width={48} 
                  height={24} 
                  rx={12} 
                  fill={`url(#capsuleGlass-${variant})`} 
                  stroke={dotColor}
                  strokeWidth={1.5}
                />
                {/* The Stubborn Whitish Shade (Exact match to bar highlights) */}
                <rect 
                  x={x - 21} 
                  y={y - 36} 
                  width={42} 
                  height={12} 
                  rx={6} 
                  fill="url(#glassShade)"
                  opacity={0.8}
                />
                <text 
                  x={x} 
                  y={y - 21} 
                  fill={textColor} 
                  fontSize={11} 
                  fontWeight="900" 
                  textAnchor="middle"
                >
                  {value}%
                </text>
              </g>
            )}
          />
        </Line>
      </LineChart>
    </ResponsiveContainer>
  );
};

export const GlassmorphicBarChart = ({ data, height = 220, variant = 'blue', keys = [{key: 'value', label: 'value'}, {key: 'value2', label: 'value2'}, {key: 'value3', label: 'value3'}] }) => {
  const schemes = {
    blue: ['#3B82F6', '#818CF8', '#FBBF24'],
    green: ['#10B981', '#34D399', '#F59E0B'],
    purple: ['#8B5CF6', '#A78BFA', '#F472B6'],
    aqua: ['#06B6D4', '#2DD4BF', '#10B981'],
    hybrid: ['#3B82F6', '#818CF8', '#2DD4BF'] // Blue base, Indigo mid, Aqua Teal top
  };
  const colors = schemes[variant] || schemes.blue;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ReBarChart 
        data={data} 
        margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
        barSize={16}
      >
        <defs>
          <linearGradient id={`glass1-${variant}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors[0]} stopOpacity={0.6}/>
            <stop offset="100%" stopColor={colors[0]} stopOpacity={0.2}/>
          </linearGradient>
          <linearGradient id={`glass2-${variant}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors[1]} stopOpacity={0.6}/>
            <stop offset="100%" stopColor={colors[1]} stopOpacity={0.2}/>
          </linearGradient>
          <linearGradient id={`glass3-${variant}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors[2]} stopOpacity={0.6}/>
            <stop offset="100%" stopColor={colors[2]} stopOpacity={0.2}/>
          </linearGradient>
        </defs>
        <XAxis 
          dataKey="date" 
          axisLine={false} 
          tickLine={false} 
          tick={{ fontSize: 9, fill: '#9CA3AF' }}
          interval={Math.floor(data.length / 4)}
        />
        <YAxis hide />
        <Tooltip 
          cursor={{ fill: '#F3F4F6', opacity: 0.4 }}
          contentStyle={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(8px)',
            borderRadius: '12px', 
            border: '1px solid rgba(255, 255, 255, 0.3)', 
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' 
          }}
        />
        {keys[0] && <Bar 
          name={keys[0].label}
          dataKey={keys[0].key} 
          stackId="a" 
          fill={`url(#glass1-${variant})`} 
          stroke={colors[0]} 
          strokeWidth={1}
          radius={keys.length === 1 ? [8, 8, 0, 0] : [0, 0, 4, 4]} 
        />}
        {keys[1] && <Bar 
          name={keys[1].label}
          dataKey={keys[1].key} 
          stackId="a" 
          fill={`url(#glass2-${variant})`} 
          stroke={colors[1]} 
          strokeWidth={1}
          radius={keys.length === 2 ? [8, 8, 0, 0] : [0, 0, 0, 0]}
        />}
        {keys[2] && <Bar 
          name={keys[2].label}
          dataKey={keys[2].key} 
          stackId="a" 
          fill={`url(#glass3-${variant})`} 
          stroke={colors[2]} 
          strokeWidth={1}
          radius={[8, 8, 0, 0]} 
        />}
      </ReBarChart>
    </ResponsiveContainer>
  );
};

export const AICircleGauge = ({ value, label, size = 160, color = "#10B981", gradientColor, suffix = "" }) => {
  const radius = 65;
  const strokeWidth = 12;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (value / 100) * circumference;
  const secondaryColor = gradientColor || color;

  return (
    <div style={{ position: 'relative', width: `${size}px`, height: `${size}px`, margin: '0 auto' }}>
      <svg height={size} width={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={`gaugeGlassFill-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.7" />
            <stop offset="100%" stopColor={secondaryColor} stopOpacity="0.2" />
          </linearGradient>
          <linearGradient id="topLight" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.9" />
            <stop offset="30%" stopColor="#FFFFFF" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
        </defs>
        <circle
          stroke="#F3F4F6"
          fill="transparent"
          strokeWidth={strokeWidth}
          r={normalizedRadius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          stroke={`url(#gaugeGlassFill-${color})`}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference + ' ' + circumference}
          style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.5s ease-in-out' }}
          strokeLinecap="round"
          r={normalizedRadius}
          cx={size / 2}
          cy={size / 2}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <circle
          stroke={color}
          fill="transparent"
          strokeWidth={1}
          strokeDasharray={circumference + ' ' + circumference}
          style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.5s ease-in-out' }}
          strokeLinecap="round"
          r={normalizedRadius + strokeWidth/2}
          cx={size / 2}
          cy={size / 2}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <circle
          stroke={color}
          fill="transparent"
          strokeWidth={1}
          strokeDasharray={circumference + ' ' + circumference}
          style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.5s ease-in-out' }}
          strokeLinecap="round"
          r={normalizedRadius - strokeWidth/2}
          cx={size / 2}
          cy={size / 2}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <circle
          stroke="url(#topLight)"
          fill="transparent"
          strokeWidth={strokeWidth - 4}
          strokeDasharray={circumference + ' ' + circumference}
          style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.5s ease-in-out' }}
          strokeLinecap="round"
          r={normalizedRadius}
          cx={size / 2}
          cy={size / 2}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          opacity={0.6}
        />
      </svg>
      <div style={{ 
        position: 'absolute', 
        top: '50%', 
        left: '50%', 
        transform: 'translate(-50%, -50%)', 
        textAlign: 'center',
        width: '100%'
      }}>
        <div style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '600', marginBottom: '2px' }}>{label}</div>
        <div style={{ fontSize: '2.25rem', fontWeight: '800', color: '#111827', lineHeight: 1 }}>{value}{suffix}</div>
      </div>
    </div>
  );
};

export const CustomDonutChart = ({ data }) => (
  <ResponsiveContainer width="100%" height={180}>
    <PieChart>
      <Pie
        data={data}
        cx="50%"
        cy="50%"
        innerRadius={45}
        outerRadius={80}
        paddingAngle={0}
        dataKey="value"
        stroke="none"
      >
        {data.map((entry, index) => (
          <Cell key={`cell-${index}`} fill={entry.color} />
        ))}
      </Pie>
      <Tooltip />
    </PieChart>
  </ResponsiveContainer>
);

export const ModernGauge = ({ value, label, sublabel, color = "#F59E0B", size = 180 }) => {
  return (
    <div style={{ position: 'relative', width: '100%', height: `${size}px` }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={[{ value: 100 }]}
            cx="50%"
            cy="50%"
            innerRadius={size * 0.35}
            outerRadius={size * 0.45}
            startAngle={225}
            endAngle={-45}
            fill="#F3F4F6"
            stroke="none"
            dataKey="value"
          />
          <Pie
            data={[{ value: value }, { value: 100 - value }]}
            cx="50%"
            cy="50%"
            innerRadius={size * 0.35}
            outerRadius={size * 0.45}
            startAngle={225}
            endAngle={225 - (value * 2.7)}
            dataKey="value"
            stroke="none"
          >
            <Cell fill={color} />
            <Cell fill="transparent" />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div style={{ 
        position: 'absolute', 
        top: '50%', 
        left: '50%', 
        transform: 'translate(-50%, -40%)', 
        textAlign: 'center' 
      }}>
        <div style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '500' }}>{label}</div>
        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', margin: '2px 0' }}>{value}</div>
        <div style={{ fontSize: '0.625rem', color: color, fontWeight: '600' }}>{sublabel}</div>
      </div>
    </div>
  );
};

export const CustomAreaChart = ({ data, color = "#3B82F6", height = 180, hideTooltip = false }) => (
  <ResponsiveContainer width="100%" height={height}>
    <ReAreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
      <defs>
        <linearGradient id={`colorGradient-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor={color === '#FFFFFF' ? '#FFFFFF' : color} stopOpacity={0.2}/>
          <stop offset="95%" stopColor={color === '#FFFFFF' ? '#FFFFFF' : color} stopOpacity={0}/>
        </linearGradient>
      </defs>
      <XAxis 
        dataKey="date" 
        axisLine={false} 
        tickLine={false} 
        tick={false}
      />
      <YAxis hide />
      {!hideTooltip && <Tooltip />}
      <Area 
        type="monotone" 
        dataKey="value" 
        stroke={color} 
        strokeWidth={1.5}
        fillOpacity={1} 
        fill={`url(#colorGradient-${color})`} 
      />
    </ReAreaChart>
  </ResponsiveContainer>
);

export const MiniAreaChart = ({ data, color = "#3B82F6" }) => (
  <ResponsiveContainer width="100%" height={60}>
    <ReAreaChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
      <defs>
        <linearGradient id={`miniGradient-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor={color} stopOpacity={0.1}/>
          <stop offset="95%" stopColor={color} stopOpacity={0}/>
        </linearGradient>
      </defs>
      <Tooltip hide />
      <Area 
        type="monotone" 
        dataKey="value" 
        stroke={color} 
        strokeWidth={2}
        fillOpacity={1} 
        fill={`url(#miniGradient-${color})`} 
        dot={false}
      />
    </ReAreaChart>
  </ResponsiveContainer>
);

export const ConcurrencyChart = ({ data }) => (
  <div style={{ borderRadius: '8px', padding: '0px', height: '250px', position: 'relative' }}>
    <div style={{ 
      position: 'absolute', 
      top: '20px', 
      left: '50%', 
      transform: 'translateX(-50%)',
      backgroundColor: '#F87171',
      padding: '8px 16px',
      borderRadius: '12px',
      color: 'white',
      zIndex: 10,
      textAlign: 'center',
      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
    }}>
      <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{data && data.length > 0 ? Math.max(...data.map(d => d.p1)) : 0} Calls</div>
      <div style={{ fontSize: '10px', opacity: 0.9 }}>Peak Load</div>
      <div style={{ 
        position: 'absolute', 
        bottom: '-5px', 
        left: '50%', 
        transform: 'translateX(-50%)', 
        width: 0, 
        height: 0, 
        borderLeft: '5px solid transparent', 
        borderRight: '5px solid transparent', 
        borderTop: '5px solid #F87171' 
      }}></div>
    </div>

    <ResponsiveContainer width="100%" height="100%">
      <ReAreaChart data={data} margin={{ top: 60, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="colorP1" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#F472B6" stopOpacity={0.8}/>
            <stop offset="95%" stopColor="#F472B6" stopOpacity={0}/>
          </linearGradient>
          <linearGradient id="colorP2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#6366F1" stopOpacity={0.8}/>
            <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
          </linearGradient>
        </defs>
        <XAxis dataKey="date" hide />
        <YAxis hide />
        <Tooltip hide />
        <Area 
          type="monotone" 
          dataKey="p1" 
          stroke="#F472B6" 
          fillOpacity={1} 
          fill="url(#colorP1)" 
        />
        <Area 
          type="monotone" 
          dataKey="p2" 
          stroke="#6366F1" 
          fillOpacity={1} 
          fill="url(#colorP2)" 
        />
      </ReAreaChart>
    </ResponsiveContainer>
    
    <div style={{ 
      position: 'absolute', 
      right: '5px', 
      top: '70%', 
      width: '12px', 
      height: '12px', 
      borderRadius: '50%', 
      backgroundColor: '#F87171', 
      border: '2px solid white' 
    }}></div>
  </div>
);

export const CustomBarChart = ({ data, horizontal = false, variant = 'default', height = 180 }) => {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ReBarChart 
        data={data} 
        layout={horizontal ? 'vertical' : 'horizontal'}
        margin={{ top: 5, right: 20, left: horizontal ? 80 : 0, bottom: 5 }}
        barGap={4}
      >
        {horizontal ? (
          <>
            <XAxis type="number" hide />
            <YAxis 
              type="category" 
              dataKey="name" 
              axisLine={false} 
              tickLine={false} 
              tick={{ fontSize: 10, fill: '#6B7280' }} 
              width={80}
            />
          </>
        ) : (
          <>
            <XAxis 
              dataKey="date" 
              axisLine={false} 
              tickLine={false} 
              tick={{ fontSize: 10, fill: '#9CA3AF' }}
              interval={Math.floor(data.length / 3)}
            />
            <YAxis type="number" hide />
          </>
        )}
        <Tooltip cursor={{ fill: 'transparent' }} />
        
        {variant === 'stacked-disconnection' && (
          <>
            <Bar dataKey="value" stackId="a" fill="#3B82F6" barSize={12} />
            <Bar dataKey="value2" stackId="a" fill="#8B5CF6" barSize={12} />
            <Bar dataKey="value3" stackId="a" fill="#F59E0B" barSize={12} />
            <Bar dataKey="value4" stackId="a" fill="#10B981" radius={[2, 2, 0, 0]} barSize={12} />
          </>
        )}

        {variant === 'stacked-sentiment' && (
          <>
            <Bar dataKey="value" stackId="a" fill="#3B82F6" barSize={12} />
            <Bar dataKey="value2" stackId="a" fill="#22D3EE" barSize={12} />
            <Bar dataKey="value3" stackId="a" fill="#F59E0B" radius={[2, 2, 0, 0]} barSize={12} />
          </>
        )}

        {variant === 'stacked-success' && (
          <>
            <Bar dataKey="value" stackId="a" fill="#3B82F6" barSize={12} />
            <Bar dataKey="value2" stackId="a" fill="#22D3EE" radius={[2, 2, 0, 0]} barSize={12} />
          </>
        )}

        {variant === 'default' && !horizontal && (
          <Bar dataKey="value" fill="#3B82F6" radius={[2, 2, 0, 0]} barSize={12} />
        )}

        {horizontal && (
          <Bar dataKey="value" fill="#60A5FA" radius={[0, 2, 2, 0]} barSize={15} />
        )}
      </ReBarChart>
    </ResponsiveContainer>
  );
};
